import React, { useState } from 'react';
import Icon from '../AppIcon';
import Button from './Button';

const NotificationCenter = ({ notifications = [], onMarkAsRead, onClearAll }) => {
  const [isOpen, setIsOpen] = useState(false);

  const unreadCount = notifications?.filter(n => !n?.read)?.length;

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'message':
        return 'MessageSquare';
      case 'video':
        return 'Video';
      case 'system':
        return 'Bell';
      case 'alert':
        return 'AlertCircle';
      default:
        return 'Bell';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'message':
        return 'text-primary';
      case 'video':
        return 'text-accent';
      case 'system':
        return 'text-secondary';
      case 'alert':
        return 'text-error';
      default:
        return 'text-muted-foreground';
    }
  };

  const formatTime = (timestamp) => {
    if (!timestamp) return '';
    const now = new Date();
    const ts = typeof timestamp === 'string' && !timestamp.endsWith('Z') && !timestamp.includes('+')
      ? timestamp + 'Z'
      : timestamp;
    const notificationTime = new Date(ts);
    const diffInMinutes = Math.floor((now - notificationTime) / 60000);

    if (diffInMinutes < 0) return 'Just now';
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes}m ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)}h ago`;
    return `${Math.floor(diffInMinutes / 1440)}d ago`;
  };

  return (
    <div className="relative">
      <Button
        variant="ghost"
        size="icon"
        className="relative"
        onClick={() => setIsOpen(!isOpen)}
      >
        <Icon name="Bell" size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-accent text-accent-foreground text-xs font-semibold rounded-full flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </Button>
      {isOpen && (
        <>
          <div 
            className="fixed inset-0 z-1010" 
            onClick={() => setIsOpen(false)}
          />
          <div className="absolute right-0 top-full mt-2 w-80 max-h-[480px] bg-popover border border-border rounded-lg shadow-xl z-1010 overflow-hidden">
            <div className="sticky top-0 bg-popover border-b border-border px-4 py-3 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Icon name="Bell" size={20} color="var(--color-primary)" />
                <h3 className="font-semibold text-foreground">Notifications</h3>
                {unreadCount > 0 && (
                  <span className="px-2 py-0.5 bg-accent text-accent-foreground text-xs font-semibold rounded-full">
                    {unreadCount}
                  </span>
                )}
              </div>
              {notifications?.length > 0 && (
                <button
                  onClick={onClearAll}
                  className="text-sm text-muted-foreground hover:text-foreground transition-smooth"
                >
                  Clear all
                </button>
              )}
            </div>

            <div className="overflow-y-auto max-h-[400px]">
              {notifications?.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-4">
                  <Icon name="BellOff" size={48} color="var(--color-muted-foreground)" />
                  <p className="mt-3 text-sm text-muted-foreground text-center">
                    No notifications yet
                  </p>
                </div>
              ) : (
                <div className="divide-y divide-border">
                  {notifications?.map((notification) => (
                    <div
                      key={notification?.id}
                      className={`px-4 py-3 hover:bg-muted transition-smooth cursor-pointer ${
                        !notification?.read ? 'bg-primary/5' : ''
                      }`}
                      onClick={() => onMarkAsRead(notification?.id)}
                    >
                      <div className="flex gap-3">
                        <div className={`flex-shrink-0 ${getNotificationColor(notification?.type)}`}>
                          <Icon name={getNotificationIcon(notification?.type)} size={20} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm font-medium text-foreground line-clamp-2">
                              {notification?.title}
                            </p>
                            {!notification?.read && (
                              <div className="flex-shrink-0 w-2 h-2 bg-accent rounded-full mt-1" />
                            )}
                          </div>
                          <p className="mt-1 text-sm text-muted-foreground line-clamp-2">
                            {notification?.message}
                          </p>
                          <p className="mt-1 text-xs text-muted-foreground data-text">
                            {formatTime(notification?.timestamp)}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {notifications?.length > 0 && (
              <div className="sticky bottom-0 bg-popover border-t border-border px-4 py-3">
                <Button
                  variant="outline"
                  fullWidth
                  size="sm"
                  onClick={() => setIsOpen(false)}
                >
                  View All Notifications
                </Button>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default NotificationCenter;